import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';

import MainContainerWithWindow from '../../../../../../components/MainContainerWithWindow/MainContainerWithWindow';
import { Loading } from '../../../../../../components/Loading/Loading';
import { pushNotify } from '../../../../../../components/Notifications';
import { useLazyDownloadReportQuery } from '../../../../../../redux/services/MerchantApi';
import { Selector, TItem } from './SelectType';
import styles from './CreateReport.module.css';

interface ReportInputs {
  type: 'deposit' | 'withdrawal';
  status: string;
  date_from: string;
  date_to: string;
}

export const CreateReport: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [downloaded, setDownloaded] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [triggerDownload, { data: reportBlob, isFetching, isError }] = useLazyDownloadReportQuery();

  const methods = useForm<ReportInputs>({
    mode: 'onSubmit',
    defaultValues: {
      type: 'deposit',
      status: 'all',
      date_from: '',
      date_to: '',
    },
  });
  const { register, handleSubmit, watch, setValue, reset } = methods;

  useEffect(() => {
  if (!open) {
    reset({
      type: 'deposit',
      status: 'all',
      date_from: '',
      date_to: '',
    });
    setDownloaded(false); // если нужно также сбрасывать флаг скачивания
  }
}, [open, reset, setDownloaded]);

  const types: TItem<'deposit' | 'withdrawal'>[] = [
    { id: 'deposit',    name: 'Пополнение' },
    { id: 'withdrawal', name: 'Вывод'    },
  ];
  const depositStatuses: TItem<'all' | 'success' | 'failure' | 'idle' | 'nf'>[] = [
    { id: 'all',     name: 'Все'     },
    { id: 'success', name: 'Успешно завершенные' },
    { id: 'failure', name: 'Отказанные и просроченные' },
    { id: 'idle',    name: 'В работе'    },
    { id: 'nf',      name: 'Реквизит не найден'      },
  ];
  const withdrawalStatuses: TItem<'all' | 'success' | 'failure' | 'in_progress' | 'problem'>[] = [
    { id: 'all',         name: 'Все'         },
    { id: 'success',     name: 'Успешно завершенные'     },
    { id: 'failure',     name: 'Отказанные и просроченные'     },
    { id: 'in_progress', name: 'В работе' },
    { id: 'problem',     name: 'В поддержке'     },
  ];
  const selectedType = watch('type');
  const onSubmit = (data: ReportInputs) => {
    setDownloaded(false);
    setDownloadStarted(true);
    triggerDownload({
      type: data.type,
      status: data.status || 'all',
      date_from: data.date_from || undefined,
      date_to: data.date_to || undefined,
    });
  };

  // Download blob only when open
  useEffect(() => {
    if (!reportBlob || downloaded || !open || !downloadStarted) return;
    const blob = reportBlob as Blob;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const ts = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
    link.download = `Report_${ts}.xlsx`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    setDownloaded(true);
    setDownloadStarted(false);
    pushNotify('Отчет успешно скачан', 'Success');
    reset({
      type: 'deposit',
      status: 'all',
      date_from: '',
      date_to: '',
    });
    onClose();
  }, [reportBlob, downloaded, reset, onClose, open]);
    useEffect(() => {
      if (reportBlob) {
        setDownloaded(false);
      }
    }, [reportBlob]);
  // Reset downloaded when opening form
  useEffect(() => {
    if (open) {
      setDownloaded(false);
    }
  }, [open]);

  if (!open) return null;

  if (isFetching) {
    return createPortal(
      <MainContainerWithWindow>
        <Loading />
      </MainContainerWithWindow>,
      document.body
    );
  }

  if (isError) {
    pushNotify('Не удалось получить отчёт', 'Error');
    onClose();
    return null;
  }

  const portal = (node: React.ReactNode) => createPortal(node, document.body);

  return portal(
    <FormProvider {...methods}>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Создать отчёт</h2>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>
        <div className={styles.loadingContainer}>
          {isFetching && <Loading />}
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <Selector
            title="Тип отчёта"
            items={types}
            value={watch('type')}
            onChange={v => setValue('type', v)}
          />
          <Selector
            title="Статус"
            items={selectedType === 'deposit' ? depositStatuses : withdrawalStatuses}
            value={watch('status')}
            onChange={v => setValue('status', v)}
          />
          <div className={styles.field}>
            <label className={styles.label}>Дата с</label>
            <input type="date" {...register('date_from')} className={styles.input} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Дата по</label>
            <input type="date" {...register('date_to')} className={styles.input} />
          </div>
          <button
            type="submit"
            className={styles.button}
            disabled={!watch('date_from') || !watch('date_to')}
          >
            Создать
          </button>
        </form>
      </div>
    </FormProvider>
  );
};
